import { lazy, Suspense } from 'react'
import { useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { paymentReads } from '../../api/fee'
import type { TransactionStatistics } from '../../api/types'
import { PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { formatNumberN } from '../../lib/format'

// echarts is ~1 MB: loaded only when a dashboard with history is shown.
const MetricChart = lazy(() => import('./DashboardCharts'))

type TotalRow = TransactionStatistics['total'][number]

/**
 * Port of `ProjectViewComponent` (`projects/:projectId`, the project
 * Dashboard): totals paid in the last 12 months per currency and two
 * time-series charts (amount, count) from
 * `GET …/payment/transactions/statistics`.
 */
export function ProjectDashboardPage() {
  const { projectId, project } = useProject()
  const q = useApiQuery(() => paymentReads.transactionStatistics(projectId), [projectId])
  const columns: Column<TotalRow>[] = [
    { key: 'currency', label: 'Currency', format: (v) => String(v ?? '').toUpperCase() },
    {
      key: 'total',
      label: 'Amount paid',
      align: 'right',
      numeric: true,
      sortValue: (r) => Number(r.total),
      format: (v) => (v === null || v === undefined ? '-' : formatNumberN(v as number, 2)),
    },
    { key: 'count', label: 'Transaction count', align: 'right', numeric: true },
  ]
  return (
    <div>
      <div className="portal-card">
        <PageTitle title={`Dashboard — ${project.name}`} />
        <QueryState loading={q.loading} error={q.error}>
          <h2>Total payment in the last 12 months by currency</h2>
          <div className="portal-table-scroll">
            <Table columns={columns} rows={q.data?.total ?? []} rowKey={(r) => r.currency} />
          </div>
          {(q.data?.total ?? []).length === 0 && <p className="portal-text-muted">No payments in the last 12 months.</p>}
        </QueryState>
      </div>
      {q.data && (q.data.history ?? []).length > 0 && (
        <div className="portal-grid-2">
          <Suspense fallback={<p className="portal-text-muted">Loading chart…</p>}>
            <MetricChart stats={q.data} metric="total" />
            <MetricChart stats={q.data} metric="count" />
          </Suspense>
        </div>
      )}
    </div>
  )
}
